import IResource from '@app/core/model/resource';
import { Injectable } from '@angular/core';
import { PageContextService } from '../page-context.service';
import { RunsStore } from '@app/core/store/runs/runs.store';
import { BaseService } from '../base.service';
import { ModalService } from '@app/shared/modals/services/modal.service';
import { catchError, distinctUntilChanged, filter, skip, switchMap, tap } from 'rxjs/operators';
import { IResourceStore } from '@app/core/store/resource/resource.store';
import { IResourceState } from '@app/core/store/resource/resource.state';
import { isNullOrUndefined } from 'util';
import { PlannedRunsStore } from '@app/core/store/runs/runs-planned.store';
import { ResourceType } from '@app/core/model/resource-type';
import { AnalysesStore } from '@app/core/store/analyses/analyses.store';
import { BioSamplesStore } from '@app/core/store/biosamples/biosamples.store';
import { LibrariesStore } from '../../store/biosamples/libraries/libraries.store';
import { ProjectsStore } from '@app/core/store/projects/projects.store';
import { DatasetsStore } from '@app/core/store/datasets/datasets.store';
import _ from 'lodash';
import { LanesStore } from '@app/core/store/runs/lanes/lanes.store';
import { SamplesStore } from '@app/core/store/samples/samples.store';
import { Observable, merge, of } from 'rxjs';
import { IBioSamplesState } from '@app/core/store/biosamples/biosamples.state';
import { ISamplesState } from '@app/core/store/samples/samples.state';
import { IProjectsState } from '@app/core/store/projects/projects.state';
import { IDatasetsState } from '@app/core/store/datasets/datasets.state';
import { IAnalysesState } from '@app/core/store/analyses/analyses.state';
import { IRunsState } from '@app/core/store/runs/runs.state';
import { ILibrariesState } from '@app/core/store/biosamples/libraries/libraries.state';
import { ILanesState } from '@app/core/store/runs/lanes/lanes.state';
import { ModalStatus } from '@app/shared/modals/model/action-modal';
import { BsSpinnerService } from '../bs-spinner/bs-spinner.service';
import { ShareModalComponent } from '@app/shared/modals/share-modal/share-modal.component';
import { CancelOwnershipTransferModalComponent } from '@app/shared/modals/cancel-ownership-transfer-modal/cancel-ownership-transfer-modal.component';
import { TransferModalComponent } from '@app/shared/modals/transfer-modal/transfer-modal.component';
import { PoolsStore } from '@app/core/store/pools/pools.store';
import { IPoolsState } from '@app/core/store/pools/pools.state';
import { RequeueLibraryModalComponent } from '@app/shared/modals/requeue-library-modal/requeue-library-modal.component';
import { RequeueLibraryPoolModalComponent } from '@app/shared/modals/requeue-library-pool-modal/requeue-library-pool-modal.component';

export interface IResourceService {
    getActiveResources(): IResource[] | IResource;
    getActiveSubResources(): IResource[] | IResource;
    getMainResourceStore(): IResourceStore<IResourceState>;
    getMainOrSubResourceStore(checkForSubResource: boolean);
    getSubResourceStore(): IResourceStore<IResourceState>;
    getActiveResourceType(): ResourceType;
    getActiveSubResourceType(): ResourceType;
    getStoreForResourceType(resourceType: ResourceType): IResourceStore<IResourceState>;
}

@Injectable({
    providedIn: 'root'
})
export class ResourceService extends BaseService implements IResourceService {

    constructor(
        private pageContextService: PageContextService,
        private modalService: ModalService,
        private bsSpinnerService: BsSpinnerService,
        private runStore: RunsStore,
        private plannedRunStore: PlannedRunsStore,
        private analysesStore: AnalysesStore,
        private bioSampleStore: BioSamplesStore,
        private sampleStore: SamplesStore,
        private librariesStore: LibrariesStore,
        private projectStore: ProjectsStore,
        private datasetsStore: DatasetsStore,
        private lanesStore: LanesStore,
        private poolsStore: PoolsStore,
    ) {
        super();
        this.init();
    }

    private init() {
        this.subs.sink = this.modalService.modalStatusChanges.pipe(
            switchMap((modalStatus: ModalStatus) => {
                const resourceStore = this.getMainOrSubResourceStore(true);

                if(resourceStore && 
                    this.modalService.wrapperComponentRef &&
                    modalStatus === ModalStatus.OPENED) {
                    return this.handleModalOpenEvent(resourceStore.getResourceType(),
                        this.modalService.wrapperComponentRef.componentType.name);
                } else {
                    return of(null);
                }
            }),
            catchError(() => {
                // We'll catch any upstream errors for now to make sure this subscription is maintained
                // for the lifetime of the app
                return of(null);
            })
        ).subscribe();

        // Modal loader/spinner subscription for resource apis 
        // NOTE: This is NOT the spinner logic for ALL modals. Some of them have custom spinner/loader logic
        // Eventually we want to expand the use of this logic to all modals to consolidate the modal spinner/logic logic
        this.subs.sink = this.modalService.modalStatusChanges.pipe(
            switchMap((modalStatus: ModalStatus) => {
                const resourceStore = this.getMainOrSubResourceStore(true);

                // Best to be as stingy as possible in regard to conditions necessary changing the spinner loading to "true" values 
                // to reduce the possibility of an unintended loader spinner appearance
                if(
                    resourceStore && 
                    modalStatus === ModalStatus.OPENED && 
                    this.modalService.wrapperComponentRef &&
                    this.isModalUsingGlobalSpinner(this.modalService.wrapperComponentRef.componentType.name)
                ) {
                    return resourceStore.loading$;
                } else {
                    return of(false);
                }
            }),
            distinctUntilChanged(),
            tap((value) => {
                if(typeof value == "boolean") {
                    this.bsSpinnerService.loadingSubject.next(value);
                }
            }),
            catchError(() => {
                return of(null);
            })
        ).subscribe();

        // Might want to implement another subscription to reset resource stores in future
    }

    isModalUsingGlobalSpinner(modalName: string): boolean {
        if(!modalName) {
            return false;
        }

        switch(modalName) {
            case ShareModalComponent.name:
            case TransferModalComponent.name:
            case CancelOwnershipTransferModalComponent.name:
                return true;
            default:
                return false;
        }
    }


    getResourceStoreChanges(resourceType: ResourceType): Observable<any> {
        const resourceStoreChanges = this.getStoreForResourceType(resourceType).stateWithPropertyChanges
          .pipe(
            filter((stateWithPropertyChanges: any) => stateWithPropertyChanges && stateWithPropertyChanges.stateChanges)
          );
    
        // Need to add initial event in case there are store changes on page before toolbar loads
        return merge(of(null), resourceStoreChanges);
      }
    
    getMultipleResourceStoresChanges(resourceTypes: ResourceType[]): Observable<any> {
        return merge(...resourceTypes.map((resourceType: ResourceType) => this.getResourceStoreChanges(resourceType)));
    }

    handleModalOpenEvent(resourceType: ResourceType, modalName: string): Observable<any> {
        let  resourceStore :IResourceStore<IResourceState>;
        resourceStore = this.getStoreForResourceType(resourceType);
        // Hack to handle pool detail toolbar actions
        // 1. edit of the Pool ->libraries-> BiosampleDefaultProject 
        // 2. Pool -> libraries -> BioSample Info 
        // 3. Pool -> BioSample libraries requeue
        // 4. Pool -> Requeue Library Pool
        if(this.isPoolDetailsPage() && (modalName != RequeueLibraryModalComponent.name || modalName != RequeueLibraryPoolModalComponent.name)) {
            resourceStore = this.getStoreForResourceType(ResourceType.BIO_SAMPLES);
            resourceType = ResourceType.BIO_SAMPLES;
        } 
       
        return resourceStore.stateChanged.pipe(
            // This skip is necessary because 'stateChanged' observable is going to automatically emit the last 'stateChanged' value once we start listening
            // This first emitted value will either be null or will contain a stale error. A null value will close the modal before the user can see anything 
            // and we obviously don't want to display a stale resource store error, thus we ignore this first value
            skip(1),
            filter((state) => !isNullOrUndefined(state)),
            tap((state: IResourceState) => {
                this.modalService.handleError(this.getResourceStoreStateError(state, resourceType));
            })
        )
    }

    getActiveResources(): IResource[] | IResource {
        const store = this.getMainResourceStore();
        if (!isNullOrUndefined(store)) {
            return store.getActiveList();
        }
    }

    getActiveSubResources(): IResource[] | IResource {
        const store = this.getSubResourceStore();
        if (!isNullOrUndefined(store)) {
            return store.getActiveList();
        }
        return null;
    }

    getMainResourceStore(): IResourceStore<IResourceState> {
        return this.getStoreForResourceType(this.getActiveResourceType());
    }

    getSubResourceStore(): IResourceStore<IResourceState> {
        return this.getStoreForResourceType(this.getActiveSubResourceType());
    }

    getMainOrSubResourceStore(checkForSubResource: boolean = false) {
        if (checkForSubResource) {
            const subResourceStore = this.getSubResourceStore();
            if (subResourceStore != null && !_.isEmpty(this.getActiveSubResources())) {
                return subResourceStore;
            }
        }
        return this.getMainResourceStore();
    }

    isAnalysesPage(): boolean {
        return (this.pageContextService.isAnalysisMasterList() || this.pageContextService.isAnalysisDetails());
    }

    isRunsPage(): boolean {
        return (this.pageContextService.isRunMasterList() || this.pageContextService.isRunDetails());
    }

    isRunDetailsPage(): boolean {
        return this.pageContextService.isRunDetails();
    }
    
    isPlannedRunsPage(): boolean {
        return this.pageContextService.isPlannedRunMasterList();
    }

    isBioSamplesPage(): boolean {
        return this.pageContextService.isBioSampleMasterList() || this.pageContextService.isBioSampleDetails();
    }
    
    isBioSampleDetailsPage(): boolean {
        return this.pageContextService.isBioSampleDetails();
    }

    isSampleDetailsPage(): boolean {
        return this.pageContextService.isSampleDetails();
    }
  
    isSubResourceBioSamplesPage(): boolean {
        return this.pageContextService.isSubResourceBioSamplesList();
    }

    isSubResourceSamplesPage(): boolean {
        return this.pageContextService.isSubResourceSamplesList();
    }

    isPoolDetailsPage(): boolean {
        return this.pageContextService.isPoolDetails();
    }

    isSubResourceDatasetsPage(): boolean {
        return this.pageContextService.isSubResourceDatasetsList();
    }

    isBioSampleListPage(): boolean {
        return this.pageContextService.isBioSampleMasterList();
    }

    isSubResourceLibrariesPage(): boolean {
        return this.pageContextService.getCurrentContextSubResourceType() === ResourceType.LIBRARIES;
    }

    isSubResourceAnalysesPage(): boolean {
        return this.pageContextService.getCurrentContextSubResourceType() === ResourceType.ANALYSIS;
    }

    isSubResourceRunsSummaryPage(): boolean {
        return (this.isRunsPage() && this.pageContextService.getCurrentContextSubResourceType() === ResourceType.LANES);
    }

    isSubResourceBiosampleRequeue(): boolean {
        return (this.isBioSamplesPage() && this.pageContextService.getCurrentContextSubResourceType() === ResourceType.REQUEUE);
    }

    isProjectsPage(): boolean {
        return this.pageContextService.isProjectMasterList() || this.pageContextService.isProjectDetails();
    }

    isProjectsMasterListPage(): boolean {
        return this.pageContextService.isProjectMasterList();
    }

    isFastqDatasetPage(): boolean {
        return this.pageContextService.isSubResourceFastqsList();
    }

    getActiveResourceType(): ResourceType {
        return this.pageContextService.getCurrentContextResourceType();
    }

    getActiveSubResourceType(): ResourceType {
        return this.pageContextService.getCurrentContextSubResourceType();
    }

    isDatasetsPage(): boolean {
        return this.pageContextService.isSubResourceDatasetsList();
    }

    getStoreForResourceType(resourceType: ResourceType): IResourceStore<IResourceState> {
        switch (resourceType) {
            case ResourceType.RUN:
            case ResourceType.RUNS:
                return this.runStore;
            case ResourceType.PLANNED_RUN:
                return this.plannedRunStore;
            case ResourceType.BIO_SAMPLE:
            case ResourceType.BIO_SAMPLES:
                return this.bioSampleStore;
            case ResourceType.SAMPLE:
            case ResourceType.SAMPLES:
                return this.sampleStore;
            case ResourceType.PROJECT:
            case ResourceType.PROJECTS:
                return this.projectStore;
            case ResourceType.DATASET:
            case ResourceType.DATASETS:
            case ResourceType.FASTQ_DATASETS:
            case ResourceType.OTHER_DATASETS:
                return this.datasetsStore;
            case ResourceType.ANALYSIS:
                return this.analysesStore;
            case ResourceType.LIBRARIES:
                return this.librariesStore;
            case ResourceType.LANES:
                return this.lanesStore;
            case ResourceType.POOLS:
                return this.poolsStore;
            default:
                return null;
        }
    }

    getResourceStoreRefreshEvents(resourceType: ResourceType): Observable<any> {
        const resourceStore = this.getStoreForResourceType(resourceType);
        switch(resourceStore.getResourceType()) {
            case ResourceType.BIO_SAMPLES:
                return (resourceStore as BioSamplesStore).bioSamplesRefreshed$;
            case ResourceType.SAMPLES:
                return (resourceStore as SamplesStore).samplesRefreshed$;
            case ResourceType.PROJECTS:
                return (resourceStore as ProjectsStore).projectsRefreshed$
            case ResourceType.DATASETS:
                return (resourceStore as DatasetsStore).datasetsRefreshed$
            case ResourceType.ANALYSIS:
                return (resourceStore as AnalysesStore).analysesListRefreshed$
            case ResourceType.RUNS:
                return (resourceStore as RunsStore).runsListRefreshed$;
            case ResourceType.POOLS:
                return (resourceStore as PoolsStore).poolsRefreshed$;
            case ResourceType.LIBRARIES:
                return (resourceStore as LibrariesStore).librariesListRefreshed$;
            default:
                throw new Error(`Resource type "${resourceType}" not implemented for "getResourceStoreRefreshEvents"`);
        }
    }

    getResourceStoreStateError(state: IResourceState, resourceType: ResourceType): any {
        const resourceStore = this.getStoreForResourceType(resourceType);
        switch(resourceStore.getResourceType()) {
            case ResourceType.BIO_SAMPLES:
                return (state as IBioSamplesState).bioSampleStateError;
            case ResourceType.SAMPLES:
                return (state as ISamplesState).sampleStateError;
            case ResourceType.PROJECTS:
                return (state as IProjectsState).projectStateError;
            case ResourceType.DATASETS:
                return (state as IDatasetsState).datasetStateError;
            case ResourceType.ANALYSIS:
                return (state as IAnalysesState).analysisStateError;
            case ResourceType.RUNS:
                return (state as IRunsState).runStateError;
            case ResourceType.LIBRARIES:
                return (state as ILibrariesState).libraryStateError;
            case ResourceType.LANES:
                return (state as ILanesState).laneStateError;
            case ResourceType.POOLS:
                return (state as IPoolsState).poolStateError;
            default:
                throw new Error(`Resource type "${resourceType}" not implemented for "getResourceStoreStateError"`);
        }
    }
}